/**
 * Generic accounting review items (backend modules/Accounting, S2a): whenever exonn
 * cannot decide a case unambiguously it records a review item instead of guessing.
 *
 * Resource: /api/accounting-review-item (index, show) and
 * POST /api/accounting-review-item/{id}/resolve with a documented decision.
 */
import type { OwnerResource } from "../../owner";
import type { AccountingAccountResource } from "../../accounting-account";
import type { AccountingPostingResource } from "../../accounting-posting";
import type { DocumentResource } from "../../document";

export enum AccountingReviewItemTypeEnum {
  MissingAccountMapping = "missing_account_mapping",
  ChartMismatch = "chart_mismatch",
  OpenItemMigrationUnclear = "open_item_migration_unclear",
  UnclassifiedZeroRate = "unclassified_zero_rate",
  Overpayment = "overpayment",
  RefundOfSettledPrepaymentRequiresTaxReview = "refund_of_settled_prepayment_requires_tax_review",
  InputTaxDocumentMissing = "input_tax_document_missing",
  InputTaxDocumentMismatch = "input_tax_document_mismatch",
  InputTaxDocumentInconsistent = "input_tax_document_inconsistent",
  UnmatchedBankTransaction = "unmatched_bank_transaction",
  DatevAccountNumberInvalid = "datev_account_number_invalid",
  TaxationMethodNotConfirmed = "taxation_method_not_confirmed",
  PendingTaxYearConfiguration = "pending_tax_year_configuration",
  VatReturnCorrectionRequired = "vat_return_correction_required",
  ClearingBalanceCarriedOver = "clearing_balance_carried_over",
  MigrationCrossAccountPosition = "migration_cross_account_position",
  MigrationAccountClassificationUnclear = "migration_account_classification_unclear",
  InvoiceAlreadyRecorded = "invoice_already_recorded",
  InvoiceDataDeviation = "invoice_data_deviation",
  OrderPaymentBasisUnclear = "order_payment_basis_unclear",
  SettlementAccountUnconfirmed = "settlement_account_unconfirmed",
  CreditNotOffset = "credit_not_offset",
}

export enum AccountingReviewItemSeverityEnum {
  Info = "info",
  Warning = "warning",
  Error = "error",
}

export enum AccountingReviewItemStatusEnum {
  Open = "open",
  Resolved = "resolved",
}

export enum AccountingReviewItemResolutionTypeEnum {
  AccountMapped = "account_mapped",
  PostingCorrected = "posting_corrected",
  CreditNote = "credit_note",
  InvoiceCorrection = "invoice_correction",
  ContractCancelled = "contract_cancelled",
  OverpaymentRefund = "overpayment_refund",
  DocumentAttached = "document_attached",
  WrittenOff = "written_off",
  TaxationMethodConfirmed = "taxation_method_confirmed",
  ReleasedWithoutDocument = "released_without_document",
  TaxYearConfigurationCompleted = "tax_year_configuration_completed",
  NoTaxEffect = "no_tax_effect",
  Other = "other",
}

/**
 * Resolution types whose decision is not self-explanatory: the backend rejects them
 * without a note. Mirrors `AccountingReviewItemResolutionTypeEnum::requiresNote()`.
 */
export const REVIEW_ITEM_RESOLUTION_TYPES_REQUIRING_NOTE: readonly AccountingReviewItemResolutionTypeEnum[] =
  [
    AccountingReviewItemResolutionTypeEnum.NoTaxEffect,
    AccountingReviewItemResolutionTypeEnum.Other,
  ];

export interface AccountingReviewItemResource {
  id: string;
  gid: string;
  owner_id: string;
  type: AccountingReviewItemTypeEnum;
  severity: AccountingReviewItemSeverityEnum;
  status: AccountingReviewItemStatusEnum;
  accounting_posting_id: string | null;
  accounting_account_id: string | null;
  vat_entry_id: string | null;
  payment_event_id: string | null;
  document_id: string | null;
  bank_transaction_id: string | null;
  amount: string | null;
  context: Record<string, unknown> | null;
  resolution_type: AccountingReviewItemResolutionTypeEnum | null;
  resolution_document_id: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  note: string | null;
  created_at: string | null;
  updated_at: string | null;
  owner?: OwnerResource | null;
  accounting_posting?: AccountingPostingResource | null;
  accounting_account?: AccountingAccountResource | null;
  document?: DocumentResource | null;
}

export type ResolveAccountingReviewItemRequestBody = {
  resolution_type: AccountingReviewItemResolutionTypeEnum;
  resolution_document_id?: string | null;
  note?: string | null;
};
